import styled from '@emotion/styled';
import MicrositeHero from '~/components/MicrositeHero';
import { DEMO_PHOTOS } from '~/demo';
import { useListingContext } from '~/editor/plugin/ListingPlugin';
import { useActiveSection, useSections } from '~/hooks/useSections';
import MicrositeRow from '~/components/MicrositeRow';
import {
	css_background,
	css_edge,
	css_fill,
	css_gap,
	css_if,
	css_xlarge,
	css_letter,
	css_medium_up,
	css_quick,
	css_slow,
	css_small_down,
	css_stroke,
	css_text,
} from '~/emotion/theme';
import { Global, css, useTheme } from '@emotion/react';
import MicrositePadding from '~/components/MicrositePadding';
import MicrositeMenuBeta from '~/components/menu/MicrositeMenuBeta';
import MicrositeOffset from '~/components/MicrositeOffset';

const COLUMN_WIDTH = css_edge(2.5);
const MOBILE_HEIGHT = '3rem';
const PANEL_HEIGHT = '8rem';

enum Layer {
	Background = 0,
	Footer = 5,
	Header = 10,
	Columns = 20,
}

const HeaderFixed = styled.header`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: ${Layer.Header};
	display: flex;
	align-items: center;
	font-weight: 500;
	background: ${css_background()};
	border-bottom: 1px solid ${css_stroke()};
	text-transform: uppercase;
	container-type: size;

	@media (${css_medium_up}) {
		height: ${COLUMN_WIDTH};
		justify-content: center;
		text-align: center;
		letter-spacing: ${css_letter('lg')};
		padding: 0 calc(${css_gap('content')} + ${COLUMN_WIDTH});
	}

	@media (${css_small_down}) {
		height: ${MOBILE_HEIGHT};
		letter-spacing: ${css_letter('sm')};
		padding: 0 4rem 0 ${css_gap('grid')};
	}
`;

const HeaderText = styled.span`
	font-size: 30cqh;
`;

const Column = styled.div`
	position: fixed;
	top: 0;
	z-index: ${Layer.Columns};
	display: flex;
	width: ${COLUMN_WIDTH};
	flex-direction: column;
	container-type: size;

	@media (${css_medium_up}) {
		align-items: center;
		background: ${css_background()};
		bottom: 0;
	}
`;

const LeftColumn = styled(Column)`
	justify-content: space-between;

	@media (${css_medium_up}) {
		left: 0;
		padding: ${css_gap('grid')} 0;
		border-right: 1px solid ${css_stroke()};
	}

	@media (${css_small_down}) {
		right: 0;
		height: ${MOBILE_HEIGHT};
		align-items: flex-end;
		justify-content: center;
		padding-right: ${css_gap('grid')};
	}
`;

const RightColumn = styled(Column)`
	right: 0;
	border-left: 1px solid ${css_stroke()};

	@media (${css_small_down}) {
		display: none;
	}
`;

const VerticalLabel = styled.span<{ accent?: boolean }>`
	writing-mode: vertical-rl;
	text-orientation: mixed;
	text-transform: uppercase;
	letter-spacing: ${css_letter('lg')};
	font-size: 20cqw;
	white-space: nowrap;
	padding: 1rem 0.4rem;
	font-weight: 500;
	color: ${css_if('accent', css_text('strong', 'green'), css_text('weak'))};
	background: ${css_if('accent', css_stroke('weak', 'green'), 'transparent')};

	@media (${css_small_down}) {
		display: none;
	}
`;

const Dots = styled.nav`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const DotButton = styled.button<{ active: boolean }>`
	position: relative;
	width: 2.5rem;
	height: 3.5rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		width: 0.65rem;
		height: 0.65rem;
		border-radius: 999px;
		border: 1px solid ${css_text()};
		background: ${css_if('active', css_text(), 'transparent')};
		transition:
			background-color ${css_quick},
			transform ${css_slow};
		transform: scale(${css_if('active', 1.5, 1)});
		transform-origin: center;
	}

	&:hover span {
		background: color-mix(in srgb, ${css_background()}, transparent 50%);
		backdrop-filter: blur(1rem);
	}

	& span {
		opacity: ${css_if('active', 1, 0)};
	}

	&:hover span,
	&:focus-visible span {
		opacity: 1;
	}
`;

const DotLabel = styled.span`
	position: absolute;
	right: calc(100% + 1.5rem);
	top: 50%;
	transform: translateY(-50%);
	padding: 0.45rem 0.7rem;
	background: ${css_background()};
	border: 1px solid ${css_stroke()};
	box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
	text-transform: uppercase;
	letter-spacing: ${css_letter('md')};
	font-size: 1rem;
	white-space: nowrap;
	transition: opacity 180ms ease;
`;

const HeroFrame = styled.div`
	position: relative;
	display: flex;
	justify-content: flex-end;
	flex-direction: column;
	background: ${css_fill()};
	height: 50vh;

	@media (${css_medium_up}) {
		flex: 1;
	}
`;

const Background = styled.div`
	position: absolute;
	inset: 0;
	z-index: ${Layer.Background};
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	background: ${css_background()};
	border: 1px solid ${css_stroke()};

	@media (${css_medium_up}) {
		padding: 0 ${css_gap('large')};
	}
`;

const Stats = styled.div`
	display: flex;
	align-items: center;
	flex-direction: row;
	flex: 1;
	gap: ${css_gap('content')};

	@media (${css_small_down}) {
		flex-direction: column;
	}
`;

const Price = styled.div`
	font-size: clamp(2rem, 4vw, 3rem);
	font-weight: 300;
	letter-spacing: 0.08em;
`;

const Divider = styled.div`
	display: none;
	width: 1px;
	align-self: stretch;
	background: ${css_stroke()};
	@media (${css_medium_up}) {
		display: block;
	}
`;

const StatGrid = styled.div`
	display: grid;
	flex: 1;
	grid-template-columns: repeat(2, max-content);
	justify-content: space-around;
	gap: ${css_gap('content')};

	@media (${css_xlarge}) {
		grid-template-columns: repeat(4, max-content);
	}
`;

const Stat = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: flex-end;
	gap: 0.45rem;
	text-transform: uppercase;
	letter-spacing: 0.14em;

	@media (${css_xlarge}) {
		justify-content: center;
	}
`;

const StatValue = styled.span`
	font-size: 1.55rem;
	font-weight: 600;
`;

const StatLabel = styled.span`
	font-size: 0.72rem;
	color: ${(props) => props.theme.color.black.text.weak};
`;

function HeroStats() {
	const listing = useListingContext();

	const stats = [
		{ label: 'beds', value: listing.beds_locale },
		{ label: 'baths', value: listing.baths_locale },
		{ label: 'sqft', value: listing.sqft_locale },
		{ label: listing.lot_acres ? 'acres' : 'sf lot', value: listing.lot_acres ? listing.lot_acres_locale : listing.lot_locale },
	].filter((stat) => stat.value);

	return (
		<Stats>
			{listing.price_formatted && <Price>${listing.price_formatted}</Price>}
			{listing.price_formatted && stats.length > 0 && <Divider />}
			{stats.length > 0 && (
				<StatGrid>
					{stats.map((stat) => (
						<Stat key={stat.label}>
							<StatValue>{stat.value}</StatValue>
							<StatLabel>{stat.label}</StatLabel>
						</Stat>
					))}
				</StatGrid>
			)}
		</Stats>
	);
}

export default function HeroBlockBeta() {
	const sections = useSections();
	const activeSectionId = useActiveSection();
	const theme = useTheme();

	return (
		<MicrositeRow full offset={`calc(${PANEL_HEIGHT} / 2)`}>
			<Global
				styles={css`
					@media (${css_medium_up({ theme })}) {
						body {
							padding: 0 ${COLUMN_WIDTH({ theme })} 0 ${COLUMN_WIDTH({ theme })};
						}

						[data-puck-component]:first-child > .row {
							padding-top: ${COLUMN_WIDTH({ theme })};
						}

						*:not([data-puck-component]) > .row:first-child {
							padding-top: ${COLUMN_WIDTH({ theme })};
						}
					}
				`}
			/>

			<HeaderFixed>
				<HeaderText>650 Eko Street</HeaderText>
			</HeaderFixed>

			<LeftColumn>
				<MicrositeMenuBeta />
				<VerticalLabel accent>New Listing!</VerticalLabel>
			</LeftColumn>

			<RightColumn>
				<VerticalLabel>Palm Springs, CA</VerticalLabel>
				<Dots>
					{sections.map((section) => (
						<DotButton key={section.id} type="button" active={activeSectionId === section.id} onClick={section.navigate}>
							<DotLabel>{section.name}</DotLabel>
						</DotButton>
					))}
				</Dots>
			</RightColumn>

			<HeroFrame>
				<Background>
					<MicrositeHero image={DEMO_PHOTOS[0]} />
				</Background>
				<MicrositePadding>
					<MicrositeOffset height={PANEL_HEIGHT} zindex={Layer.Footer}>
						<Footer>
							<HeroStats />
						</Footer>
					</MicrositeOffset>
				</MicrositePadding>
			</HeroFrame>
		</MicrositeRow>
	);
}
